import { useState } from "react";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import {
  Formik,
  Form,
  Field,
  FieldInputProps,
  FormikProps,
  ErrorMessage,
} from "formik";
import * as Yup from "yup";

import AuthFlowLayout from "../../layouts/AuthFlowLayout";
import GradientButton from "../../components/GradientButton";
import EmailInput from "../../components/EmailInput";
import ButtonContainer from "../../components/ButtonContainer";
import Loader from "../../components/Loader";
import { resetPasswordAtom } from "../../atoms/userAtoms";
import PrimaryColorButton from "../../components/PrimaryColorButton";
import { useAuthService } from "../../api/authService";

const ForgotPassword = () => {
  const [stage, setStage] = useState(1);
  const [formData, setFormData] = useAtom(resetPasswordAtom);
  const [isRequesting, setIsRequesting] = useState(false);
  const navigate = useNavigate();
  const { initPasswordReset } = useAuthService();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
  });

  const handleSubmit = async (values: typeof formData) => {
    try {
      setIsRequesting(true);
      const result = await initPasswordReset(values.email);

      if (result.success) {
        setFormData(values);
        setStage(2);
      }
    } catch (err) {
      console.error("Password reset request error:", err);
    } finally {
      setIsRequesting(false);
    }
  };

  const renderStageHeader = () => {
    switch (stage) {
      case 1:
        return {
          headerText: "Reset your password",
          subheaderText:
            "Enter your email address and we'll send you instructions to reset your password.",
        };
      case 2:
        return {
          headerText: "Email Sent",
          subheaderText:
            "Check your inbox for an email with a link and instructions to reset your password.",
        };
      default:
        return {};
    }
  };

  const stageHeader = renderStageHeader();

  return (
    <AuthFlowLayout
      headerText={stageHeader.headerText}
      subheaderText={stageHeader.subheaderText}
      isLoading={isRequesting}
      loader={<Loader />}
    >
      <Formik
        initialValues={formData}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            {/* Stage 1: User Details */}
            {stage === 1 && (
              <>
                <Field name="email">
                  {({
                    field,
                  }: {
                    field: FieldInputProps<any>;
                  }) => {
                    return (
                      <div>
                        <EmailInput
                          {...field}
                          label="Email"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-red-500 text-xs my-1"
                        />
                      </div>
                    );
                  }}
                </Field>
                <ButtonContainer
                  firstButton={
                    <PrimaryColorButton
                      type="button"
                      text="Back"
                      onClick={() => {
                        navigate("/login");
                      }}
                    />
                  }
                  secondButton={
                    <GradientButton
                      type="submit"
                      text="Send Link"
                      disabled={isSubmitting}
                    />
                  }
                />
              </>
            )}

            {/* Stage 2: Success */}
            {stage === 2 && (
              <ButtonContainer
                firstButton={
                  <PrimaryColorButton
                    type="button"
                    text="Back to Login"
                    onClick={() => {
                      navigate("/login");
                    }}
                  />
                }
              />
            )}
          </Form>
        )}
      </Formik>
    </AuthFlowLayout>
  );
};

export default ForgotPassword;
